import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { Global } from '../common/global';
import gsap from 'gsap';

// --- CONSTANTS ---
const MAX_SPEED = 0.1;
const ARRIVAL_DISTANCE = 2;
const MAX_ROTATION_RATE = Math.PI / 2;
const SMOOTHNESS_FACTOR = 0.05;
const TRANSITION_TOLERANCE = 0.08;
const RAYCAST_DISTANCE = 10;
const RAYCAST_OFFSET = 3;
const COLLISION_FRONT_TOLERANCE = 0.1;
const YIELD_CHANCE = 0.5; // 50% chance to attempt a yield movement

// --- DEBUG TOGGLE ---
const DEBUG_COLLISIONS = false;

export default class Car {
  // Constructor takes a scene and camera as parameters
  constructor(scene, currentJunctionId, carKey, carIndex) {
    this.scene = scene;
    this.carKey = carKey;
    this.carIndex = carIndex;

    this.onEvents = this.onEvents.bind(this);
    this.canCheckCollision = false;
    // Kinematic state
    this.input = { forward: false, left: false, right: false };
    this.targetRotationY = 0;
    this.currentSpeed = 0;
    this.isBlocked = false;

    // 💥 Collision Properties
    this.boundingBox = new THREE.Box3();
    this.boxHelper = null;

    // --- Raycasting Setup ---
    this.raycaster = new THREE.Raycaster();
    this.raycastDirection = new THREE.Vector3(0, 0, 1);

    // --- Pathfinding State ---
    this.carDirection = {
      1: [5],
      2: [12],
      3: [7],
      4: [20],
      5: [3],
      6: [2],
      7: [32],
      8: [4],
      9: [11],
      10: [37],
      11: [1, 15],
      12: [14, 15],
      13: [10],
      14: [10],
      15: [16],
      16: [40, 19],
      17: [13],
      18: [13, 40],
      19: [20, 6],
      20: [44, 24],
      21: [18, 19],
      22: [21, 44],
      23: [24],
      24: [26, 27],
      25: [22],
      26: [25, 29],
      27: [8],
      28: [26, 8],
      29: [30, 31],
      30: [48],
      31: [28, 32],
      32: [35],
      33: [30, 28],
      34: [33],
      35: [56],
      36: [33],
      37: [39, 38],
      38: [57],
      39: [17, 40],
      40: [42, 43],
      41: [38, 39],
      42: [41, 61],
      43: [44, 21],
      44: [46, 47],
      45: [42, 43],
      46: [64, 45],
      47: [25, 48],
      48: [50, 51],
      49: [46, 47],
      50: [49, 79],
      51: [52],
      52: [54, 55],
      53: [50, 51],
      54: [83],
      55: [36],
      56: [54],
      57: [58, 59],
      58: [68],
      59: [41],
      60: [58],
      61: [62, 63],
      62: [71],
      63: [45, 64],
      64: [65, 66],
      65: [84, 75],
      66: [49],
      67: [65],
      68: [70],
      69: [9],
      70: [71, 60],
      71: [73, 74],
      72: [69, 70],
      73: [72],
      74: [84, 75],
      75: [77, 78],
      76: [73, 74],
      77: [76],
      78: [67, 79],
      79: [81, 82],
      80: [77, 78],
      81: [80],
      82: [53],
      83: [81],
      84: [62, 63],
    };

    this.breakFactor = {
      value: 0,
    };
    this.currentJunctionId = currentJunctionId;
    this.targetJunctionId = this.carDirection[currentJunctionId][0];
    this.previousJunctionId = null;
    this.targetPosition = null;
    this.isMoving = false;
  }

  // Initialization method for setting up event listeners and initial state
  init(gui) {
    this.events = EventDispatcher.getObj();
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents);
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents);
    this.events.addEventListener('GAME_EVENTS', this.onEvents);
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'update':
        this.update(data['message']['data'].delta);
        break;
      case 'car_stop_and_proceed':
        this.stopAndProceed(data['message']['data']);
        break;
      case 'create_scene':
        this.addCars();
        break;
      case 'dispose':
        this.dispose();
        break;
    }
  }

  stopAndProceed(data, byForce = false) {
    const { uuid } = data;

    if ((uuid != this.car.uuid && !byForce) || this.isBraking) return;

    this.isBraking = true;
    this.itemToCheck.userData.isMoving = false;
    this.breakReleaseTwn && this.breakReleaseTwn.kill();
    this.breakApplyTwn = gsap.to(this.breakFactor, {
      value: 1,
      duration: byForce ? 1 : 0.5,
      ease: 'power2.out',
      onComplete: function () {
        this.isBraking = false;
      }.bind(this),
    });
    this.breakReleaseTwn = gsap.to(this.breakFactor, {
      value: 0,
      duration: byForce ? 1 : 0.5,
      delay: byForce ? 1 : 0.5,
      onComplete: function () {
        this.itemToCheck.userData.isMoving = true;
      }.bind(this),
      ease: 'power2.in',
    });
    //breakFactor
    // this.car.visible = false;
  }
  addCars() {
    this.isBraking = false;
    setTimeout(() => {
      this.car =
        Global.assets['glbs'][`car${this.carKey}`]['asset'].scene.clone();
      this.scene.add(this.car);

      // Store carKey in userData for collision resolution checks
      this.car.userData = {
        isGhost: false,
        carKey: this.carKey, // ⬅️ Added carKey here
      };

      // 1. Initial Position Setup
      const startPoint = Global.junctions[`point_${this.currentJunctionId}`]; //.position;
      this.car.position.copy(startPoint);

      // 2. Initial Target Setup
      this.setNextTarget(this.targetJunctionId);

      this.isMoving = true;

      let itemFound = false;
      this.car.traverse((item) => {
        if (item.isMesh) {
          if (!itemFound) {
            itemFound = true;
            Global.cars.push(item);
            this.itemToCheck = item;
            this.itemToCheck.material.side = THREE.DoubleSide;
            item.userData.isMoving = true;
          }
          item.material.envMapIntensity = Math.min(1, Global.envIntensity);
          item.material.map.colorSpace = THREE.SRGBColorSpace;
          item.material.map.encoding = THREE.sRGBEncoding;

          item.castShadow = true;
          // item.receiveShadow = true;

          if (item.name.includes('back_tyre')) {
            this.rearWheel = item;
          } else if (item.name.includes('front_tyre')) {
            this.frontWheel = item;
          } else {
            item.material.metalness = 1;
            item.material.roughness = 0.4;
          }
        }
      });

      // 💥 COLLISION: Initial Bounding Box Calculation
      this.boundingBox.setFromObject(this.car);

      // 🎁 DEBUG: Setup the BoxHelper
      if (DEBUG_COLLISIONS) {
        this.boxHelper = new THREE.BoxHelper(this.car, 0xff0000);
        this.scene.add(this.boxHelper);
      }
    }, 100);
  }

  /**
   * Sets the target position and calculates the direction vector.
   */
  setNextTarget(junctionId) {
    if (!Global.junctions[`point_${junctionId}`]) {
      console.error(
        `Junction point_${junctionId} not found in Global.junctions.`
      );
      this.isMoving = false;
      return;
    }

    this.targetJunctionId = junctionId;
    this.targetPosition = { ...Global.junctions[`point_${junctionId}`] }; //.position.clone();
    this.targetPosition.y += 0.2;
  }
  checkCollision() {
    const origin = this.car.position.clone();
    origin.y += 0.2;
    const direction = new THREE.Vector3(0, 0, 1);
    direction.applyQuaternion(this.car.quaternion);
    direction.normalize();
    this.raycaster.set(origin, direction);
    this.raycaster.far = 10;

    const cars = Global.cars.filter((item) => item !== this.itemToCheck);
    const intersects = this.raycaster.intersectObjects(cars, false);

    if (intersects.length > 0 && intersects[0].object.userData.isMoving) {
      this.stopAndProceed(
        {
          uuid: this.car.uuid,
        },
        true
      );
    }
  }
  /**
   * Main update loop for movement and logic.
   * @param {number} delta - The time elapsed since the last frame.
   */
  update(delta) {
    if (!Global.isGameActive) return;

    if (this.car && this.carIndex == Global.carInspectIndex) {
      this.canCheckCollision = !this.canCheckCollision;
      this.canCheckCollision && this.checkCollision();
    }

    if (!this.car || !this.isMoving || !this.targetPosition) {
      return;
    }

    // --- 2. Movement Calculations ---
    const currentPos = this.car.position;

    const moveDirection = new THREE.Vector3().subVectors(
      this.targetPosition,
      currentPos
    );
    const distanceToTarget = moveDirection.length();

    // --- 3. Check for Arrival ---
    if (distanceToTarget < ARRIVAL_DISTANCE) {
      if (distanceToTarget < TRANSITION_TOLERANCE) {
        this.previousJunctionId = this.currentJunctionId;
        this.currentJunctionId = this.targetJunctionId;

        let possibleNextIds = this.carDirection[this.currentJunctionId];

        if (possibleNextIds && possibleNextIds.length > 0) {
          const validNextIds = possibleNextIds.filter(
            (id) => id !== this.previousJunctionId
          );

          let nextId;
          if (validNextIds.length > 0) {
            nextId =
              validNextIds[Math.floor(Math.random() * validNextIds.length)];
          } else {
            nextId = this.previousJunctionId;
          }

          this.setNextTarget(nextId);
        } else {
          this.isMoving = false;
          return;
        }
      }
    }

    // --- 4. Move the Car ---
    if (this.isMoving) {
      moveDirection.normalize();

      // Set speed based on block state (set by raycast or hard collision/yield logic)
      this.currentSpeed = MAX_SPEED * (1 - this.breakFactor.value);

      if (this.currentSpeed > 0) {
        let maxMoveDistance = this.currentSpeed * Global.delta;
        const moveDistance = Math.min(maxMoveDistance, distanceToTarget);
        const displacement = moveDirection.multiplyScalar(moveDistance * 1);

        // Rotate wheels based on movement
        if (this.frontWheel) this.frontWheel.rotation.x += moveDistance * 1.5;
        if (this.rearWheel) this.rearWheel.rotation.x += moveDistance * 1.5;

        // Apply the movement
        this.car.position.add(displacement);
      }

      // --- 5. Rotation/Orientation (Smooth and Limited Turn Logic) ---
      const targetAngle = Math.atan2(moveDirection.x, moveDirection.z);
      const currentAngle = this.car.rotation.y;

      let angleDifference = targetAngle - currentAngle;
      angleDifference =
        THREE.MathUtils.euclideanModulo(
          angleDifference + Math.PI,
          Math.PI * 2
        ) - Math.PI;

      const smoothedDifference =
        angleDifference * SMOOTHNESS_FACTOR * Global.delta;
      const maxTurn = MAX_ROTATION_RATE * Global.delta * 10;
      let rotationStep = Math.min(Math.abs(smoothedDifference), maxTurn);

      if (smoothedDifference < 0) {
        rotationStep = -rotationStep;
      }

      this.car.rotation.y += rotationStep;

      this.car.rotation.y = THREE.MathUtils.euclideanModulo(
        this.car.rotation.y,
        Math.PI * 2
      );
    }
  }
  dispose() {
    this.breakApplyTwn && this.breakApplyTwn.kill();
    this.breakReleaseTwn && this.breakReleaseTwn.kill();

    if (this.car) {
      this.car.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => material.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
      this.scene.remove(this.car);
      this.car = null;
    }
    if (this.events) {
      this.events.removeEventListener('GAMESCENE_EVENTS', this.onEvents);
      this.events.removeEventListener('PLAYER_EVENTS', this.onEvents);
      this.events.removeEventListener('GAME_EVENTS', this.onEvents);
    }
  }
}
